/**
 * The pure JSON paste pipeline behind the export menu's paste paths: parse →
 * detect format → validate → convert/normalize, returning a result the paste
 * path reports.
 *
 * Pipeline: `JSON.parse` → if the value has a `walls` array it's a RawFloorPlan
 * scan (`RawFloorPlanSchema` + `convertRawFloorPlan` + report summary), else
 * `FloorPlanRoomSchema` + `normalizeShape`. Errors come back as located,
 * human-readable messages: a caret under the offending character for syntax
 * errors, `path → message` lines for Zod issues.
 */

import { normalizeShape } from './constraints';
import type { ConversionOptions, ConversionReport } from './raw';
import { convertRawFloorPlan } from './raw';
import type { FloorPlanRoom, RawFloorPlan } from './schemas';
import { FloorPlanRoomSchema, RawFloorPlanSchema } from './schemas';

/** `error` is the exception behind a failed conversion, kept for its stack. */
export type ParseFloorPlanResult = { ok: boolean; shape?: FloorPlanRoom; message: string; error?: Error };

/** Parse, validate, and normalize pasted JSON text into an applicable room. */
export function parseFloorPlan(text: string): ParseFloorPlanResult {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch (e) {
    return { ok: false, message: describeJsonSyntaxError(e as Error, text) };
  }

  if (looksLikeRawPlan(data)) return convertRawFloorPlanToFloorPlan(data);

  const result = FloorPlanRoomSchema.safeParse(data);
  if (!result.success) {
    return { ok: false, message: describeSchemaIssues(result.error.issues, 'Not a valid room') };
  }

  // Schema-valid, but still run the geometry repair backstop (clamps negatives,
  // fixes out-of-range spans) so an applied room is always renderable.
  // The schema-inferred type is too deep for TS to relate to FloorPlanRoom (TS2589);
  // the runtime shapes are identical, so a cast is safe here.
  const shape = normalizeShape(result.data as unknown as FloorPlanRoom);
  return { ok: true, shape, message: 'Applied' };
}

/** Validate and convert a pasted RawFloorPlan scan. */
export function convertRawFloorPlanToFloorPlan(data: unknown, options: Partial<ConversionOptions> = {}): ParseFloorPlanResult {
  const result = RawFloorPlanSchema.safeParse(data);
  if (!result.success) {
    return { ok: false, message: describeSchemaIssues(result.error.issues, "Looks like a raw scan, but it's not valid") };
  }
  try {
    const { room, report } = convertRawFloorPlan(result.data as unknown as RawFloorPlan, options);
    const shape = normalizeShape(room);
    return { ok: true, shape, message: `Converted from raw scan + applied${describeReport(report)}` };
  } catch (e) {
    return { ok: false, message: `Raw scan conversion failed: ${(e as Error).message}`, error: e as Error };
  }
}

/**
 * Turn a `JSON.parse` SyntaxError into a located, human-readable message: the
 * 1-based line/column plus the offending line with a caret under it.
 */
function describeJsonSyntaxError(err: Error, text: string): string {
  const m = /position (\d+)/.exec(err.message);
  if (!m) return `Invalid JSON: ${err.message}`;
  const pos = Math.min(Number(m[1]), text.length);
  let line = 1, col = 1;
  for (let i = 0; i < pos; i++) {
    if (text[i] === '\n') {
      line++;
      col = 1;
    } else col++;
  }
  const lineText = text.split('\n')[line - 1] ?? '';
  const caret = ' '.repeat(Math.max(0, col - 1)) + '^';
  return `Invalid JSON at line ${line}, column ${col}:\n${lineText}\n${caret}`;
}

/** A raw scan is recognized by its `walls` array — the room format has none. */
function looksLikeRawPlan(data: unknown): boolean {
  return typeof data === 'object' && data !== null && Array.isArray((data as { walls?: unknown }).walls);
}

/** One status line per noteworthy conversion outcome (empty string if clean). */
function describeReport(report: ConversionReport): string {
  const lines: string[] = [];
  const count = (n: number, what: string) => {
    if (n) lines.push(`${n} ${what}`);
  };
  count(report.droppedWallIds.length, 'wall(s) dropped');
  count(report.culledSurfaceIds.length, 'noise surface(s) culled');
  count(report.absorbedOpeningIds.length, 'alcove-mouth opening(s) absorbed');
  count(report.discardedOpeningIds.length, 'opening(s) DISCARDED (no matching wall)');
  lines.push(...report.warnings);
  return lines.length ? `\n  • ${lines.join('\n  • ')}` : '';
}

/** Format Zod issues as `path → message` lines (root paths shown as "(root)"). */
function describeSchemaIssues(issues: { path: PropertyKey[]; message: string }[], what: string): string {
  const head = `${what} (${issues.length} ${issues.length === 1 ? 'problem' : 'problems'}):`;
  const lines = issues.slice(0, 12).map(i => {
    const path = i.path.map(String).join('.') || '(root)';
    return `  • ${path} → ${i.message}`;
  });
  if (issues.length > 12) lines.push(`  …and ${issues.length - 12} more`);
  return [head, ...lines].join('\n');
}
